import { type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { formatAmount } from '../../lib/format';
import type { CombatStats, ResourceKey, Severity } from '../../lib/game';

export type StatAccent = ResourceKey | Severity | 'neutral';

const TEXT: Record<StatAccent, string> = {
  titan: 'text-titan',
  silicate: 'text-silicate',
  isotope: 'text-isotope',
  power: 'text-power',
  aether: 'text-aether',
  credit: 'text-credit',
  chronos: 'text-chronos',
  danger: 'text-danger',
  warning: 'text-warning',
  success: 'text-success',
  info: 'text-info',
  neutral: 'text-content',
};

export interface StatProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  label: ReactNode;
  value: ReactNode;
  icon?: ReactNode;
  accent?: StatAccent;
  /** Secondary line under the value — a rate, delta or capacity. */
  hint?: ReactNode;
  align?: 'left' | 'right' | 'center';
  size?: 'sm' | 'md';
}

/** Labelled readout tile. The atom every stat block is built from. */
export function Stat({
  label,
  value,
  icon,
  accent = 'neutral',
  hint,
  align = 'left',
  size = 'md',
  className,
  ...rest
}: StatProps) {
  return (
    <div
      className={cn(
        'min-w-0',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        className,
      )}
      {...rest}
    >
      <div
        className={cn(
          'flex items-center gap-1 font-display text-label-sm uppercase text-content-muted',
          align === 'right' && 'justify-end',
          align === 'center' && 'justify-center',
        )}
      >
        {icon}
        <span className="truncate">{label}</span>
      </div>
      <div
        data-sg-numeric
        className={cn(
          'mt-0.5 font-semibold leading-tight',
          size === 'sm' ? 'text-readout' : 'text-readout-lg',
          TEXT[accent],
        )}
      >
        {value}
      </div>
      {hint ? <div className="mt-0.5 text-label text-content-muted">{hint}</div> : null}
    </div>
  );
}

export interface StatGridProps extends HTMLAttributes<HTMLDivElement> {
  columns?: 2 | 3 | 4;
}

/** Responsive grid of Stat tiles. */
export function StatGrid({ columns = 4, className, children, ...rest }: StatGridProps) {
  return (
    <div
      className={cn(
        'grid gap-2 sm:gap-3',
        columns === 2 && 'grid-cols-2',
        columns === 3 && 'grid-cols-2 sm:grid-cols-3',
        columns === 4 && 'grid-cols-2 sm:grid-cols-4',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export interface CombatStatsBlockProps extends HTMLAttributes<HTMLDivElement> {
  stats: CombatStats;
  /** Compact single-row layout for list rows; full grid for detail cards. */
  dense?: boolean;
}

/**
 * The canonical hull / shield / damage / cargo readout.
 *
 * Order and colour are fixed across every ship and defence card so players can
 * compare two units at a glance without re-reading the labels.
 */
export function CombatStatsBlock({ stats, dense = false, className, ...rest }: CombatStatsBlockProps) {
  const entries: Array<{ label: string; value: number; accent: StatAccent }> = [
    { label: 'Hülle', value: stats.hull, accent: 'titan' },
    { label: 'Schild', value: stats.shield, accent: 'silicate' },
    { label: 'Schaden', value: stats.damage, accent: 'danger' },
  ];
  if (stats.cargo !== undefined) {
    entries.push({ label: 'Fracht', value: stats.cargo, accent: 'credit' });
  }

  if (dense) {
    return (
      <div className={cn('flex flex-wrap items-center gap-x-3 gap-y-1', className)} {...rest}>
        {entries.map((entry) => (
          <span key={entry.label} className="inline-flex items-baseline gap-1">
            <span className="font-display text-label-sm uppercase text-content-muted">
              {entry.label}
            </span>
            <span data-sg-numeric className={cn('text-xs font-semibold', TEXT[entry.accent])}>
              {formatAmount(entry.value)}
            </span>
          </span>
        ))}
      </div>
    );
  }

  return (
    <div
      className={cn('grid grid-cols-2 gap-2 sm:grid-cols-4', className)}
      {...rest}
    >
      {entries.map((entry) => (
        <Stat
          key={entry.label}
          size="sm"
          label={entry.label}
          accent={entry.accent}
          value={formatAmount(entry.value)}
        />
      ))}
    </div>
  );
}
